<template>
  <v-container fluid class="py-4">
    <v-row justify="center">
      <v-col cols="12" md="8" lg="8">
        <v-card outlined class="rounded-lg pa-6 elevation-1">
          <!-- Card Title Header -->
          <div class="d-flex align-center mb-4 pb-2 border-bottom">
            <v-icon color="cyan" class="mr-2">mdi-lock-reset</v-icon>
            <span class="text-h6 font-weight-bold grey--text text--darken-3"
              >Update Password</span
            >
          </div>

          <v-form
            ref="passwordForm"
            v-model.trim="formValid"
            lazy-validation
            v-on:submit.stop.prevent="onSubmit"
          >
            <!-- Old Password Field (if applicable) -->
            <template v-if="isOldPassword">
              <div
                class="subtitle-2 font-weight-bold grey--text text--darken-2 mb-1"
              >
                Old Password
              </div>
              <v-text-field
                :append-icon="show1 ? 'mdi-eye' : 'mdi-eye-off'"
                :type="show1 ? 'text' : 'password'"
                v-on:click:append="show1 = !show1"
                dense
                outlined
                solo
                flat
                prepend-inner-icon="mdi-lock-clock"
                placeholder="Enter current password"
                color="cyan"
                v-model.trim="formData.old_password"
                :rules="[
                  validateRules.required(formData.old_password, 'Old Password'),
                ]"
              ></v-text-field>
            </template>

            <!-- New Password Field -->
            <div
              class="subtitle-2 font-weight-bold grey--text text--darken-2 mb-1 mt-2"
            >
              New Password
            </div>
            <v-text-field
              :append-icon="show2 ? 'mdi-eye' : 'mdi-eye-off'"
              :type="show2 ? 'text' : 'password'"
              v-on:click:append="show2 = !show2"
              dense
              outlined
              solo
              flat
              prepend-inner-icon="mdi-lock-outline"
              @keydown.space="(event) => event.preventDefault()"
              placeholder="Enter new password"
              color="cyan"
              v-model.trim="formData.password"
              v-on:keypress="(e) => manageLimit(e)"
              @input="validatePassword($event)"
              :rules="[
                validateRules.required(formData.password, 'New Password'),
              ]"
            ></v-text-field>

            <!-- Confirm Password Field -->
            <div
              class="subtitle-2 font-weight-bold grey--text text--darken-2 mb-1 mt-2"
            >
              Confirm Password
            </div>
            <v-text-field
              :append-icon="show3 ? 'mdi-eye' : 'mdi-eye-off'"
              :type="show3 ? 'text' : 'password'"
              v-on:click:append="show3 = !show3"
              dense
              outlined
              solo
              flat
              prepend-inner-icon="mdi-lock-check-outline"
              :readonly="disabledDate()"
              placeholder="Confirm new password"
              color="cyan"
              v-model.trim="formData.password_confirmation"
              v-on:keypress="(e) => manageLimitCon(e)"
              :rules="[
                validateRules.required(
                  formData.password_confirmation,
                  'Confirm Password'
                ),
                validateRules.confirmPassword(
                  formData.password_confirmation,
                  'Confirm Password',
                  formData.password
                ),
              ]"
            ></v-text-field>

            <!-- Password Requirements Box -->
            <div class="mt-4 pa-4 rounded-lg grey lighten-5 border">
              <div
                class="caption-1 font-weight-bold grey--text text--darken-2 mb-2 text-uppercase"
              >
                <v-icon x-small color="cyan" class="mr-1"
                  >mdi-shield-check-outline</v-icon
                >
                Password Requirements
              </div>
              <div class="d-flex flex-column gap-1">
                <div
                  class="d-flex align-center font-size-12 my-1"
                  :class="
                    has_min ? 'success--text font-weight-medium' : 'grey--text'
                  "
                >
                  <v-icon
                    x-small
                    :color="has_min ? 'success' : 'grey lighten-1'"
                    class="mr-2"
                  >
                    {{
                      has_min
                        ? "mdi-check-circle"
                        : "mdi-checkbox-blank-circle-outline"
                    }}
                  </v-icon>
                  The password must be at least 8 characters
                </div>

                <div
                  class="d-flex align-center font-size-12 my-1"
                  :class="
                    has_uppercase
                      ? 'success--text font-weight-medium'
                      : 'grey--text'
                  "
                >
                  <v-icon
                    x-small
                    :color="has_uppercase ? 'success' : 'grey lighten-1'"
                    class="mr-2"
                  >
                    {{
                      has_uppercase
                        ? "mdi-check-circle"
                        : "mdi-checkbox-blank-circle-outline"
                    }}
                  </v-icon>
                  Has a capital letter
                </div>

                <div
                  class="d-flex align-center font-size-12 my-1"
                  :class="
                    has_lowercase
                      ? 'success--text font-weight-medium'
                      : 'grey--text'
                  "
                >
                  <v-icon
                    x-small
                    :color="has_lowercase ? 'success' : 'grey lighten-1'"
                    class="mr-2"
                  >
                    {{
                      has_lowercase
                        ? "mdi-check-circle"
                        : "mdi-checkbox-blank-circle-outline"
                    }}
                  </v-icon>
                  Has a lowercase letter
                </div>

                <div
                  class="d-flex align-center font-size-12 my-1"
                  :class="
                    has_number
                      ? 'success--text font-weight-medium'
                      : 'grey--text'
                  "
                >
                  <v-icon
                    x-small
                    :color="has_number ? 'success' : 'grey lighten-1'"
                    class="mr-2"
                  >
                    {{
                      has_number
                        ? "mdi-check-circle"
                        : "mdi-checkbox-blank-circle-outline"
                    }}
                  </v-icon>
                  Has a number
                </div>

                <div
                  class="d-flex align-center font-size-12 my-1"
                  :class="
                    has_special
                      ? 'success--text font-weight-medium'
                      : 'grey--text'
                  "
                >
                  <v-icon
                    x-small
                    :color="has_special ? 'success' : 'grey lighten-1'"
                    class="mr-2"
                  >
                    {{
                      has_special
                        ? "mdi-check-circle"
                        : "mdi-checkbox-blank-circle-outline"
                    }}
                  </v-icon>
                  Has a special character
                </div>
              </div>
            </div>

            <!-- Submit Button -->
            <div class="mt-5 text-right" v-if="!hideInternalBtn">
              <v-btn
                depressed
                :disabled="isSubmitDisabled"
                :loading="formLoading"
                v-on:click="onSubmit"
                class="custom-bold-button text-capitalize px-6 white--text"
                color="cyan"
              >
                <v-icon left small>mdi-key-change</v-icon>
                Update Password
              </v-btn>
            </div>
          </v-form>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>
<style scoped>
.frmValidation {
  font-size: 13px;
  margin-bottom: 6px !important;
}
.frmValidation--passed {
  color: #717b85;
}
.frmIcon {
  color: #eb0029;
}
.frmValidation--passed .frmIcon {
  color: #0fa140;
}

.howToBuild {
  text-align: center;
  color: purple;
}
.howToBuild a {
  color: grey;
  font-weight: bold;
  text-decoration: none;
  text-transform: uppercase;
}
</style>
<script>
import ValidationMixin from "@/core/plugins/validation-mixin";
import { PATCH } from "@/core/services/store/request.module";
export default {
  name: "password-update",
  mixins: [ValidationMixin],
  props: {
    user: {
      type: Number,
      default: 0,
      required: true,
    },
    isOldPassword: {
      type: Boolean,
      default: false,
    },
    hideInternalBtn: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      show1: false,
      show2: false,
      show3: false,
      formValid: true,
      has_number: false,
      has_lowercase: false,
      has_uppercase: false,
      has_special: false,
      disabledData: true,
      has_min: false,
      formLoading: false,
      formData: {
        old_password: null,
        password: null,
        password_confirmation: null,
      },
    };
  },
  methods: {
    validatePassword(e) {
      let inputVal = "";
      if (typeof e === "string") {
        inputVal = e;
      } else if (this.formData.password) {
        inputVal = String(this.formData.password);
      } else if (e && e.key) {
        inputVal = String(e.key);
      }
      let a = /[0-9]+/;
      let b = /[a-z]/;
      let c = /[A-Z]/;
      let d = /[!@#$%^&*)(+=._-]/;
      this.has_number = a.test(inputVal);
      this.has_lowercase = b.test(inputVal);
      this.has_uppercase = c.test(inputVal);
      this.has_special = d.test(inputVal);
      if (inputVal && inputVal.length > 7) {
        this.has_min = true;
      } else {
        this.has_min = false;
      }

      if (
        this.has_number &&
        this.has_lowercase &&
        this.has_uppercase &&
        this.has_special &&
        this.has_min
      ) {
        this.formValid = true;
      } else {
        this.formValid = false;
      }
    },

    manageLimit(e) {
      // this.disabledDate();
      if (this.formData.password && this.formData.password.length > 15) {
        e.preventDefault();
      }
    },
    disabledDate() {
      if (
        this.has_number &&
        this.has_lowercase &&
        this.has_uppercase &&
        this.has_special &&
        this.has_min
      ) {
        return false;
      }
      return true;
    },
    manageLimitCon(e) {
      if (this.formData.password && this.formData.password.length > 15) {
        e.preventDefault();
      }
    },
    reset() {
      if (this.$refs.passwordForm) {
        this.$refs.passwordForm.resetValidation();
      }
      this.formData = {
        old_password: null,
        password: null,
        password_confirmation: null,
      };
      this.has_number = false;
      this.has_lowercase = false;
      this.has_uppercase = false;
      this.has_special = false;
      this.has_min = false;
      this.formValid = false;
      this.formLoading = false;
    },
    onSubmit() {
      const _this = this;
      if (!_this.$refs.passwordForm.validate()) {
        return false;
      }

      _this.formLoading = true;
      _this.$emit("loading", true);

      let url = "user/" + _this.user + "/password";

      if (_this.isOldPassword) {
        url = "user/reset-password";
      }

      _this.$store
        .dispatch(PATCH, {
          url,
          data: _this.formData,
        })
        .then(() => {
          _this.formValid = true;
          _this.formData = {
            old_password: null,
            password: null,
            password_confirmation: null,
          };
          (this.has_number = false),
            (this.has_lowercase = false),
            (this.has_uppercase = false),
            (this.has_special = false),
            (this.has_min = false),
            _this.$emit("password:updated");
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.formLoading = false;
          _this.$emit("loading", false);
        });
    },
  },
  computed: {
    isSubmitDisabled() {
      const isPasswordMatching =
        Boolean(this.formData.password) &&
        Boolean(this.formData.password_confirmation) &&
        this.formData.password === this.formData.password_confirmation;
      const isOldPasswordValid =
        !this.isOldPassword || Boolean(this.formData.old_password);

      return (
        !this.formValid ||
        this.formLoading ||
        this.disabledDate() ||
        !isPasswordMatching ||
        !isOldPasswordValid
      );
    },
  },
  watch: {
    isSubmitDisabled: {
      immediate: true,
      handler(val) {
        this.$emit("disabled", val);
      },
    },
  },
};
</script>
